const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Interfaces pour la gestion des projets
export interface ConceptionStep {
  id: number;
  project_service: number;
  name: string;
  description?: string;
  order_index: number;
  status: 'pending' | 'in_progress' | 'completed';
  status_display: string;
  estimated_duration?: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  created_by_name?: string;
}

export interface ServiceOrder {
  id: number;
  project: string;
  project_service: number;
  project_service_name: string;
  order_index: number;
  created_at: string;
  updated_at: string;
  conception_steps: ConceptionStep[];
}

export interface ProjectService {
  id: number;
  project: string;
  service_name?: string;
  pack_name?: string;
  type: 'service' | 'pack';
  quantity: number;
  unit_price: number;
  total_price: number;
  description?: string;
  pages?: number;
  created_at: string;
  conception_steps: ConceptionStep[];
  order: ServiceOrder[];
}

export interface ProjectManagement {
  project_id: string;
  purchase_request: number;
  client: number;
  client_name: string;
  client_email: string;
  client_phone?: string;
  client_company?: string;
  client_address?: string;
  status: string;
  invoice_id: string;
  total_amount: number;
  discount_percentage: number;
  description?: string;
  notes?: string;
  estimated_duration?: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  created_by_name?: string;
  services: ProjectService[];
  service_orders: ServiceOrder[];
}

export interface AvailableService {
  id: number;
  name: string;
  price: number;
  price_type: string;
}

export interface AvailablePack {
  id: number;
  name: string;
  total_price: number;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  company?: string;
}

export interface CreateProjectData {
  project_name: string;
  client_data: {
    user_id?: number;
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
    address?: string;
  };
  services: Array<{
    type: 'service' | 'pack';
    service_id?: number;
    pack_id?: number;
    quantity: number;
    unit_price: number;
    pages?: number;
    description?: string;
  }>;
  description?: string;
}

class ProjectManagementApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    console.log('🔑 Token utilisé pour gestion projets:', token ? `${token.substring(0, 20)}...` : 'AUCUN TOKEN');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  // Récupérer tous les projets pour la gestion (Admin uniquement)
  async getProjectsManagement(): Promise<ProjectManagement[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des projets:', error);
      throw error;
    }
  }

  // Récupérer les projets de l'utilisateur connecté avec les étapes de conception
  async getUserProjectsWithSteps(): Promise<ProjectManagement[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/user/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des projets utilisateur:', error);
      throw error;
    }
  }

  // Mettre à jour l'ordre des services dans un projet
  async updateServiceOrder(projectId: string, serviceOrders: Array<{project_service_id: number, order_index: number}>): Promise<ProjectManagement> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/service-order/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          service_orders: serviceOrders
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'ordre des services:', error);
      throw error;
    }
  }

  // Créer une nouvelle étape de conception
  async createConceptionStep(projectServiceId: number, stepData: {
    name: string;
    description?: string;
    estimated_duration?: number;
  }): Promise<ConceptionStep> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/conception-steps/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          project_service_id: projectServiceId,
          ...stepData
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'étape:', error);
      throw error;
    }
  }

  // Mettre à jour une étape de conception
  async updateConceptionStep(stepId: number, stepData: Partial<ConceptionStep>): Promise<ConceptionStep> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/conception-steps/${stepId}/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(stepData),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'étape:', error);
      throw error;
    }
  }

  // Supprimer une étape de conception
  async deleteConceptionStep(stepId: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/conception-steps/${stepId}/delete/`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'étape:', error);
      throw error;
    }
  }

  // Réorganiser l'ordre des étapes de conception
  async reorderConceptionSteps(projectServiceId: number, stepOrders: Array<{step_id: number, order_index: number}>): Promise<ConceptionStep[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/conception-steps/reorder/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          project_service_id: projectServiceId,
          step_orders: stepOrders
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la réorganisation des étapes:', error);
      throw error;
    }
  }

  // Créer un projet manuellement
  async createProjectManually(projectData: CreateProjectData): Promise<ProjectManagement> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/create/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      throw error;
    }
  }

  // Récupérer les services et packs disponibles
  async getAvailableServicesAndPacks(): Promise<{services: AvailableService[], packs: AvailablePack[]}> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/services-packs/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des services et packs:', error);
      throw error;
    }
  }

  // Rechercher des utilisateurs
  async searchUsers(query: string): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects-management/search-users/?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      throw error;
    }
  }
}

export const projectManagementApiService = new ProjectManagementApiService();
